import {
    FC,
    useMemo,
    useState,
    ReactNode,
    useEffect,
    useContext,
    useCallback,
    createContext,
} from 'react';
import serverVars from 'server-vars';
import { useFragment, graphql } from 'react-relay';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { CUSTOMER_TYPE_TRADE } from '../../../constants/userConstants';
import {
    addLoginHandlers,
    sendChatMessageOnPageRefetch,
} from 'dibs-buyer-layout/exports/loginHandlers';
import { useDblExternalSelector } from 'dibs-buyer-layout/exports/DblExternalContext';
import {
    getUserZipCode,
    getUserSessionRegions,
    getUserSessionCountryCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers';

// types
import { PdpRespUnavailableContextProvider_item$key as Item } from './__generated__/PdpRespUnavailableContextProvider_item.graphql';
import { PdpRespUnavailableLayoutEntryQuery$variables as RelayVars } from '../__generated__/PdpRespUnavailableLayoutEntryQuery.graphql';

type Refetch = (vars: $TSFixMe, options: { onComplete: (error: Error | null) => void }) => void;

type PdpRespUnavailableContextType = {
    refetch: Refetch;
    currency: string;
    relayVars: RelayVars;
};

const relayDefaults = {
    userId: '',
    itemId: '',
    itemIds: [''],
    isTrade: false,
    fetchPdp: false,
    fetchChat: false,
    fetchUser: false,
    afterDisplay: false,
    fetchRegionalInfo: false,
};

const PdpRespUnavailableContext = createContext<PdpRespUnavailableContextType>({
    currency: '',
    refetch: () => {},
    relayVars: relayDefaults,
});

const itemFragment = graphql`
    fragment PdpRespUnavailableContextProvider_item on Item {
        serviceId
        seller {
            isAccessible
        }
    }
`;

export const PdpRespUnavailableContextProvider: FC<{
    item: Item;
    refetch: Refetch;
    children: ReactNode;
}> = ({ refetch, item: itemRef, children }) => {
    const item = useFragment(itemFragment, itemRef);
    const currency = useDblExternalSelector<$TSFixMe, string>(store => store.currency.currency);
    const [relayVars, setRelayVars] = useState({
        ...relayDefaults,
        itemId: item?.serviceId || '',
        itemIds: [item?.serviceId || ''],
    });
    const isSellerAccessible = !!item?.seller?.isAccessible;

    const handleRefetch = useCallback(() => {
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const isTrade = userType === CUSTOMER_TYPE_TRADE;
        const updatedRelayVars = {
            userId,
            isTrade,
            fetchPdp: isTrade,
            afterDisplay: true,
            fetchUser: !!userId,
            fetchChat: isSellerAccessible,
            userZipCode: getUserZipCode(),
            fetchRegionalInfo: !getUserSessionRegions(),
            userCountryCode: getUserSessionCountryCode(),
        };
        refetch(updatedRelayVars, {
            onComplete: error => {
                if (!error) {
                    setRelayVars(() => ({
                        ...serverVars.get('relayVars'),
                        ...updatedRelayVars,
                    }));
                    sendChatMessageOnPageRefetch();
                }
            },
        });
    }, [refetch, isSellerAccessible]);

    //After refetching the item will change and this will trigger the refetch again
    //Firing the useEffect only once will prevent unnecessary refetches
    useEffect(() => {
        handleRefetch();
        addLoginHandlers(() => {
            handleRefetch();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({
            refetch,
            currency,
            relayVars,
        }),
        [refetch, currency, relayVars]
    );

    return (
        <PdpRespUnavailableContext.Provider value={value}>
            {children}
        </PdpRespUnavailableContext.Provider>
    );
};

export const useRespUnavailableContext = (): PdpRespUnavailableContextType => {
    return useContext(PdpRespUnavailableContext);
};
